import { useState } from 'react';
import { Outlet, Link, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { 
  Menu, 
  X, 
  LayoutDashboard,
  User,
  MessageSquare,
  MessagesSquare,
  Plane,
  Flag,
  Settings,
  LogOut,
  ChevronLeft,
  Shield,
  Bell,
  Rss
} from 'lucide-react';
import { cn } from '../lib/utils';

const CaddieHat = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M4 16s0-4 8-4 8 4 8 4" />
    <path d="M2 16h20" />
    <ellipse cx="12" cy="12" rx="6" ry="4" />
    <path d="M6 12c0-3.3 2.7-6 6-6s6 2.7 6 6" />
  </svg>
);

const DashboardLayout = () => {
  const { user, golfer, logout, isAdmin } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const location = useLocation();

  const navItems = [
    { href: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
    { href: '/feed', label: 'The Clubhouse', icon: Rss },
    { href: '/profile', label: 'The Locker Room', icon: User },
    { href: '/messages', label: 'Messages', icon: MessageSquare },
    { href: '/chats', label: 'Chat Rooms', icon: MessagesSquare },
    { href: '/travel', label: 'Away Games', icon: Plane },
    { href: '/rounds', label: 'Rounds', icon: Flag },
    { href: '/caddie', label: 'Caddie', icon: CaddieHat },
    { href: '/notifications', label: 'Notifications', icon: Bell },
  ];

  const adminItems = [
    { href: '/admin', label: 'Admin Console', icon: Shield },
  ];

  const isActive = (href) => {
    if (href === '/admin') {
      return location.pathname.startsWith('/admin');
    }
    return location.pathname === href;
  };

  return (
    <div className="min-h-screen bg-deep-green">
      {/* Mobile Header */}
      <header className="lg:hidden fixed top-0 left-0 right-0 z-50 bg-deep-green border-b border-white/10">
        <div className="flex items-center justify-between h-16 px-4">
          <button
            onClick={() => setSidebarOpen(true)}
            className="text-white p-2"
            data-testid="mobile-sidebar-toggle"
          >
            <Menu className="w-6 h-6" />
          </button>
          <Link to="/" className="flex items-center gap-2">
            <img 
              src="/logo.png" 
              alt="Fairway Alliance"
              className="h-8 w-auto"
            />
            <span className="text-white font-display font-bold">Fairway Alliance</span>
          </Link>
          <div className="w-10" />
        </div>
      </header>

      {/* Mobile Sidebar Overlay */}
      {sidebarOpen && (
        <div 
          className="lg:hidden fixed inset-0 bg-black/50 z-50"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside 
        className={cn(
          "fixed top-0 left-0 bottom-0 z-50 bg-deep-green border-r border-white/10 transition-all duration-300",
          "lg:translate-x-0",
          sidebarOpen ? "translate-x-0" : "-translate-x-full",
          sidebarCollapsed ? "lg:w-20" : "lg:w-64",
          "w-64"
        )}
      >
        {/* Sidebar Header */}
        <div className="h-16 lg:h-20 flex items-center justify-between px-4 border-b border-white/10">
          {!sidebarCollapsed && (
            <Link to="/" className="flex items-center gap-2">
              <img 
                src="/logo.png" 
                alt="Fairway Alliance"
                className="h-10 w-auto"
              />
              <span className="text-white font-display font-bold">Fairway</span>
            </Link>
          )}
          {sidebarCollapsed && (
            <Link to="/" className="mx-auto">
              <img 
                src="/logo.png" 
                alt="Fairway Alliance"
                className="h-10 w-auto"
              />
            </Link>
          )}
          <button
            onClick={() => setSidebarOpen(false)}
            className="lg:hidden text-white/60 hover:text-white p-1"
          >
            <X className="w-5 h-5" />
          </button>
          <button
            onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
            className="hidden lg:block text-white/60 hover:text-white p-1"
            data-testid="collapse-sidebar"
          >
            <ChevronLeft className={cn("w-5 h-5 transition-transform", sidebarCollapsed && "rotate-180")} />
          </button>
        </div>

        {/* User Info */}
        <div className={cn(
          "px-4 py-4 border-b border-white/10",
          sidebarCollapsed && "px-2"
        )}>
          <div className={cn("flex items-center gap-3", sidebarCollapsed && "justify-center")}>
            <div className="w-10 h-10 rounded-full bg-gold/20 flex items-center justify-center shrink-0">
              {golfer?.photo_url ? (
                <img src={golfer.photo_url} alt="" className="w-full h-full rounded-full object-cover" />
              ) : (
                <User className="w-5 h-5 text-gold" />
              )}
            </div>
            {!sidebarCollapsed && (
              <div className="overflow-hidden">
                <p className="text-white font-semibold truncate">
                  {golfer?.display_name || user?.display_name || 'Member'}
                </p>
                <p className="text-cream/60 text-xs truncate">
                  {golfer?.skill_level ? `${golfer.skill_level} golfer` : 'Golfer'}
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Navigation */}
        <nav className="p-4 space-y-1">
          {navItems.map((item) => (
            <Link
              key={item.href}
              to={item.href}
              onClick={() => setSidebarOpen(false)}
              className={cn(
                "flex items-center gap-3 px-3 py-2.5 rounded-sm transition-all",
                isActive(item.href)
                  ? "bg-gold text-deep-green"
                  : "text-white/70 hover:text-white hover:bg-white/5",
                sidebarCollapsed && "justify-center px-2"
              )}
              data-testid={`sidebar-${item.label.toLowerCase().replace(' ', '-')}`}
            >
              <item.icon className="w-5 h-5 shrink-0" />
              {!sidebarCollapsed && <span className="font-body">{item.label}</span>}
            </Link>
          ))}

          {isAdmin && (
            <>
              <div className={cn("my-4 border-t border-white/10", sidebarCollapsed && "mx-2")} />
              {adminItems.map((item) => (
                <Link
                  key={item.href}
                  to={item.href}
                  onClick={() => setSidebarOpen(false)}
                  className={cn(
                    "flex items-center gap-3 px-3 py-2.5 rounded-sm transition-all",
                    isActive(item.href)
                      ? "bg-gold text-deep-green"
                      : "text-white/70 hover:text-white hover:bg-white/5",
                    sidebarCollapsed && "justify-center px-2"
                  )}
                  data-testid={`sidebar-${item.label.toLowerCase().replace(' ', '-')}`}
                >
                  <item.icon className="w-5 h-5 shrink-0" />
                  {!sidebarCollapsed && <span className="font-body">{item.label}</span>}
                </Link>
              ))}
            </>
          )}
        </nav>

        {/* Bottom Actions */}
        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-white/10">
          <Button
            variant="ghost"
            onClick={logout}
            className={cn(
              "w-full justify-start text-white/70 hover:text-white hover:bg-white/5",
              sidebarCollapsed && "justify-center px-2"
            )}
            data-testid="sidebar-logout"
          >
            <LogOut className="w-5 h-5 mr-3" />
            {!sidebarCollapsed && "Sign Out"}
          </Button>
        </div>
      </aside>

      {/* Main Content */}
      <main 
        className={cn(
          "min-h-screen transition-all duration-300 pt-16 lg:pt-0",
          sidebarCollapsed ? "lg:pl-20" : "lg:pl-64"
        )}
      >
        <div className="p-4 md:p-6 lg:p-8">
          <Outlet />
        </div>
      </main>
    </div>
  );
};

export default DashboardLayout;
